import { ContractStatusBadge } from '@/components/ContractStatusBadge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { isContractStatusCancellable } from '@/lib/contractUtils';
import { Info } from 'lucide-react';
import type { ContractDetail } from '../types';

interface ContractsTableProps {
  contracts: ContractDetail[];
  selectedContracts: Set<string>; // Set of contract numbers
  onContractSelectionChange: (
    contractNumber: string,
    isSelected: boolean
  ) => void;
  isAllOrNoneSelected: 'all' | 'none' | 'some';
  onViewDetails?: (contract: ContractDetail) => void;
}

export function ContractsTable({
  contracts,
  selectedContracts,
  onContractSelectionChange,
  isAllOrNoneSelected,
  onViewDetails,
}: ContractsTableProps) {
  if (!contracts || contracts.length === 0) {
    return null; // Or a "No contracts to display" message, handled by parent
  }

  let headerCheckboxState: boolean | 'indeterminate' = false;
  if (isAllOrNoneSelected === 'all') {
    headerCheckboxState = true;
  } else if (isAllOrNoneSelected === 'some') {
    headerCheckboxState = 'indeterminate';
  }
  // If 'none', it remains false (initial value)

  const handleToggleSelectContracts = (checked: boolean) => {
    const selectableContractsToProcess = contracts.filter((contract) =>
      isContractStatusCancellable(contract.status)
    );

    if (selectableContractsToProcess.length === 0) {
      return; // No selectable contracts to toggle
    }

    selectableContractsToProcess.forEach((contract) => {
      onContractSelectionChange(contract.contractNumber, checked);
    });
  };

  return (
    <Table className='contract-search-results-table'>
      <TableHeader>
        <TableRow>
          <TableHead className='w-[50px]'>
            <Checkbox
              checked={headerCheckboxState}
              onCheckedChange={handleToggleSelectContracts}
              aria-label='Select all contracts'
            />
          </TableHead>
          <TableHead>Contract</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Issuing Dealer</TableHead>
          <TableHead>Effective Date</TableHead>
          <TableHead>Lender</TableHead>
          <TableHead>Customer</TableHead>
          <TableHead>Vehicle</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {contracts.map((contract) => (
          <TableRow key={contract.contractNumber}>
            <TableCell>
              {isContractStatusCancellable(contract.status) && (
                <Checkbox
                  checked={selectedContracts.has(contract.contractNumber)}
                  onCheckedChange={(checked: boolean) =>
                    onContractSelectionChange(contract.contractNumber, checked)
                  }
                  aria-label={`Select contract ${contract.contractNumber}`}
                />
              )}
            </TableCell>
            <TableCell>{contract.contractNumber}</TableCell>
            <TableCell>
              <div className='flex items-center gap-2'>
                <ContractStatusBadge status={contract.status} />
                {contract.status.toLowerCase() === 'cancelled' &&
                  onViewDetails && (
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => onViewDetails(contract)}
                      className='h-6 w-6 p-0 text-primary hover:text-primary/80 hover:bg-primary/10 transition-colors duration-200'
                      aria-label={`View details for contract ${contract.contractNumber}`}
                      title='View cancellation details'
                    >
                      <Info className='size-5' />
                    </Button>
                  )}
              </div>
            </TableCell>
            <TableCell>{contract.productTypeName}</TableCell>
            <TableCell>{contract.issuingDealer}</TableCell>
            <TableCell>{contract.effectiveDate}</TableCell>
            <TableCell>{contract.lenderName}</TableCell>
            <TableCell>{contract.customerFullName}</TableCell>
            <TableCell>
              {contract.vehicleInfo.year} {contract.vehicleInfo.make}{' '}
              {contract.vehicleInfo.model} (VIN: {contract.vehicleInfo.vin})
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
